/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { RightOutlined } from '@ant-design/icons'
import {
  MyPlayer,
  MyTeamOfficial,
  UpcomingGame,
} from '@sportninja/common/types/PlayerAttendance'
import {
  cardContainerStyle,
  headerStyle,
  titleStyle,
  viewMoreStyle,
  viewMoreTextStyle,
  counterTeamTextStyle,
} from './styles'
import TeamsContainer from './components/TeamsContainer'
import GameInfoComponent from './components/GameInfo'
import AttendanceQuestion from './components/AttendanceQuestion'
import ManageTeamAttendance from './components/ManageTeamAttendance'
import AttendanceCounter from './components/AttendanceCounter'
import SnacksInfo from './components/SnacksInfo'
import VideographerInfo from './components/VideographerInfo'

/**
 * Props for the GameAttendanceCard component
 */
export interface GameAttendanceCardProps {
  /** Whether the game time is to be defined */
  isGameTimeToBeDefined: boolean
  /** Date string displayed when time is TBD */
  gameDateWithoutTime: string
  /** Home team display data */
  homeTeam: {
    name: string
    logo: string
  }
  /** Away team display data */
  awayTeam: {
    name: string
    logo: string
  }
  /** Formatted game date/time string */
  gameTime: string
  /** Name of the league/schedule */
  leagueName: string
  /** Venue name */
  arenaName: string
  /** Facility number/name */
  facilityNumber: string
  /** Callback when a player selects an attendance status */
  onAttendanceSelect: ({
    playerId,
    gameId,
    rosterId,
    status,
  }: {
    playerId: string
    gameId: string
    rosterId: string
    status: number
  }) => void
  /** Whether the user is playing on the home team */
  isPlayingOnHomeTeam: boolean
  /** Whether the user is playing on the away team */
  isPlayingOnAwayTeam: boolean
  /** Whether to display the player attendance buttons */
  displayPlayerAttendance: boolean
  /** Whether to display the team rep attendance buttons */
  displayTeamRepAttendance: boolean
  /** List of players to display attendance for */
  playersAttendance: MyPlayer[]
  /** List of team officials to display manage buttons for */
  teamRepsAttendance: MyTeamOfficial[]
  /** Full game data object */
  rowItem: UpcomingGame
  /** Whether this is the first game in the carousel */
  isFirstGame: boolean
  /** Callback when manage attendance button is pressed */
  onPressManageAttendance: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  /** Callback when manage refreshments button is pressed */
  onPressManageRefreshments: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    showWaiver: boolean
  ) => void
  /** Callback when manage videographer button is pressed */
  onPressManageVideographer?: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
}

/**
 * GameAttendanceCard displays an upcoming game with team matchup, attendance controls,
 * team attendance counters, and drinks/snacks assignments.
 *
 * Features:
 * - Team logos and names with VS separator
 * - Game time, league, and arena information
 * - Player attendance selection (YES / MAYBE / NO)
 * - Team rep manage attendance button
 * - Attendance counter summary (accepted, tentative, declined, unknown)
 * - Drinks/snacks assignment display and management
 *
 * @param {GameAttendanceCardProps} props - Component props
 * @returns {JSX.Element} Game attendance card
 */
const GameAttendanceCard: React.FC<GameAttendanceCardProps> = ({
  isFirstGame,
  rowItem,
  homeTeam,
  awayTeam,
  gameTime,
  leagueName,
  arenaName,
  facilityNumber,
  onAttendanceSelect,
  isPlayingOnHomeTeam,
  isPlayingOnAwayTeam,
  displayPlayerAttendance,
  displayTeamRepAttendance,
  playersAttendance,
  teamRepsAttendance,
  onPressManageAttendance,
  onPressManageRefreshments,
  onPressManageVideographer,
  isGameTimeToBeDefined = false,
  gameDateWithoutTime = null,
}) => {
  const isTeamRepOnHomeTeam = useMemo(() => {
    if (!teamRepsAttendance || teamRepsAttendance?.length === 0) {
      return false
    }
    const teamRep = teamRepsAttendance.find(
      (t) => t.team_id === rowItem?.home_team?.id
    )
    return !!teamRep
  }, [teamRepsAttendance, rowItem])

  const isTeamRepOnAwayTeam = useMemo(() => {
    if (!teamRepsAttendance || teamRepsAttendance?.length === 0) {
      return false
    }
    const teamRep = teamRepsAttendance.find(
      (t) => t.team_id === rowItem?.visiting_team?.id
    )
    return !!teamRep
  }, [teamRepsAttendance, rowItem])

  return (
    <div css={cardContainerStyle} key={rowItem.id}>
      <div css={headerStyle}>
        <h2 css={titleStyle}>
          {isFirstGame ? 'Next Game' : 'Ready for your game?'}
        </h2>
        <a href={`/game/${rowItem.id}`}>
          <div css={viewMoreStyle}>
            <p css={viewMoreTextStyle}>View more</p>
            <RightOutlined style={{ fontSize: '12px' }} />
          </div>
        </a>
      </div>
      <a href={`/game/${rowItem.id}`}>
        <TeamsContainer homeTeam={homeTeam} awayTeam={awayTeam} />
        <GameInfoComponent
          isGameTimeToBeDefined={isGameTimeToBeDefined}
          gameDateWithoutTime={gameDateWithoutTime}
          gameTime={gameTime}
          leagueName={leagueName}
          arenaName={arenaName}
          facilityNumber={facilityNumber}
        />
      </a>

      {displayPlayerAttendance ? (
        <AttendanceQuestion
          playersAttendance={playersAttendance}
          rowItem={rowItem}
          onAttendanceSelect={onAttendanceSelect}
        />
      ) : null}

      {displayTeamRepAttendance && teamRepsAttendance.length > 0 ? (
        <ManageTeamAttendance
          teamRepsAttendance={teamRepsAttendance}
          rowItem={rowItem}
          isPlayingOnHomeTeam={isPlayingOnHomeTeam}
          onPressManageAttendance={onPressManageAttendance}
        />
      ) : null}

      {isPlayingOnHomeTeam && rowItem?.home_team?.attendance_numbers ? (
        <>
          <div style={{ height: 16 }} />
          {isPlayingOnHomeTeam && isPlayingOnAwayTeam ? (
            <p css={counterTeamTextStyle}>{rowItem?.home_team?.name}</p>
          ) : null}
          <AttendanceCounter
            attendanceNumbers={rowItem.home_team.attendance_numbers}
          />
        </>
      ) : null}

      {isPlayingOnAwayTeam && rowItem?.visiting_team?.attendance_numbers ? (
        <>
          <div style={{ height: 16 }} />
          {isPlayingOnHomeTeam && isPlayingOnAwayTeam ? (
            <p css={counterTeamTextStyle}>{rowItem?.visiting_team?.name}</p>
          ) : null}
          <AttendanceCounter
            attendanceNumbers={rowItem.visiting_team.attendance_numbers}
          />
        </>
      ) : null}

      {isPlayingOnHomeTeam ? (
        <SnacksInfo
          isTeamRepOnTeam={isTeamRepOnHomeTeam}
          team={rowItem?.home_team}
          teamNameSuffix={
            isPlayingOnHomeTeam && isPlayingOnAwayTeam
              ? `(${rowItem?.home_team?.name})`
              : ''
          }
          onPressManageRefreshments={onPressManageRefreshments}
          gameId={rowItem?.id}
          showWaiver={rowItem?.show_waiver}
        />
      ) : null}

      {isPlayingOnAwayTeam ? (
        <SnacksInfo
          isTeamRepOnTeam={isTeamRepOnAwayTeam}
          team={rowItem?.visiting_team}
          teamNameSuffix={
            isPlayingOnHomeTeam && isPlayingOnAwayTeam
              ? `(${rowItem?.visiting_team?.name})`
              : ''
          }
          onPressManageRefreshments={onPressManageRefreshments}
          gameId={rowItem?.id}
          showWaiver={rowItem?.show_waiver}
        />
      ) : null}

      {isPlayingOnHomeTeam &&
      rowItem?.show_video &&
      onPressManageVideographer ? (
        <VideographerInfo
          isTeamRepOnTeam={isTeamRepOnHomeTeam}
          team={rowItem?.home_team}
          teamNameSuffix={
            isPlayingOnHomeTeam && isPlayingOnAwayTeam
              ? `(${rowItem?.home_team?.name})`
              : ''
          }
          onPressManageVideographer={onPressManageVideographer}
          gameId={rowItem?.id}
          showVideo={true}
          teamSide='home'
        />
      ) : null}

      {isPlayingOnAwayTeam &&
      rowItem?.show_video &&
      onPressManageVideographer ? (
        <VideographerInfo
          isTeamRepOnTeam={isTeamRepOnAwayTeam}
          team={rowItem?.visiting_team}
          teamNameSuffix={
            isPlayingOnHomeTeam && isPlayingOnAwayTeam
              ? `(${rowItem?.visiting_team?.name})`
              : ''
          }
          onPressManageVideographer={onPressManageVideographer}
          gameId={rowItem?.id}
          showVideo={true}
          teamSide='visitor'
        />
      ) : null}
    </div>
  )
}

export default React.memo(GameAttendanceCard)
